/**
 * PATH: packages/email-hub/src/feature/lib/categoryConfig.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-CATEGORYCONFIG-001
 * PURPOSE: Canonical body of this email lib module. Lifted
 *          from apps/<tenant>/src/features/email/lib/categoryConfig.ts
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md).
 *          Tenants import this via thin re-export shims; edit
 *          THIS file, not the tenant copies.
 * WHY:     Removes operator-visible drift between tenants —
 *          previously every email feature change had to be
 *          mirrored manually in two tenant trees, and concurrent
 *          agents kept letting one side go stale. One canonical
 *          body, both tenants consume from this package.
 * RELATED:
 *   - apps/<tenant>/src/features/email/lib/categoryConfig.ts: thin re-export
 *     shim that points back here.
 */

import {
  AlertCircle,
  Ban,
  Bell,
  Briefcase,
  CheckCircle,
  DollarSign,
  FileSignature,
  FileText,
  HardHat,
  Mail,
  Megaphone,
  Newspaper,
  Receipt,
  Send,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  Timer,
  Truck,
  User,
  Users,
} from 'lucide-react'

export type CategoryConfig = {
  icon: typeof FileText
  label: string
  pill: string
  dot: string
  ring: string
  /** Concrete hex of the category hue (Tailwind ``*-500``/``*-400`` to
   *  match ``dot``). Used for inline-styled surfaces that can't take a
   *  Tailwind class — e.g. the EmailRow card's top+left edge accent +
   *  tint, which is colour-matched to the category. */
  hex: string
}

/**
 * Each entry has:
 *   - ``icon``  — Lucide icon shown in the chip + row-leading badge
 *   - ``label`` — human-readable name shown in chip + filter bar
 *   - ``pill``  — full pill style (used in the inline row badge)
 *   - ``dot``   — solid background + text colour for the leading dot
 *   - ``ring``  — outline used by the active filter chip in the
 *                 always-on category bar above the email list.
 *
 * Palette discipline: stick to Tailwind's *-500/*-600 hue + 100/900-30
 * surface so light & dark mode both stay legible.
 */
export const CATEGORY_CONFIG: Record<string, CategoryConfig> = {
  quote_request:    { icon: FileText,      label: 'Quote Request',    pill: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',         dot: 'bg-blue-500',    hex: '#3b82f6', ring: 'ring-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-300' },
  quote_submission: { icon: Send,          label: 'Quote Sent',       pill: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300', dot: 'bg-indigo-500',  hex: '#6366f1', ring: 'ring-indigo-500/40 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300' },
  contract:         { icon: FileSignature, label: 'Contract',         pill: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300', dot: 'bg-violet-500',  hex: '#8b5cf6', ring: 'ring-violet-500/40 bg-violet-500/10 text-violet-700 dark:text-violet-300' },
  bill:             { icon: Receipt,       label: 'Supplier Bill',    pill: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300', dot: 'bg-orange-500',  hex: '#f97316', ring: 'ring-orange-500/40 bg-orange-500/10 text-orange-700 dark:text-orange-300' },
  invoice:          { icon: DollarSign,    label: 'Our Invoice',      pill: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',     dot: 'bg-green-500',   hex: '#22c55e', ring: 'ring-green-500/40 bg-green-500/10 text-green-700 dark:text-green-300' },
  payment:          { icon: CheckCircle,   label: 'Payment',          pill: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', dot: 'bg-emerald-500', hex: '#10b981', ring: 'ring-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' },
  supplier:         { icon: ShoppingBag,   label: 'Supplier',         pill: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',         dot: 'bg-cyan-500',    hex: '#06b6d4', ring: 'ring-cyan-500/40 bg-cyan-500/10 text-cyan-700 dark:text-cyan-300' },
  delivery:         { icon: Truck,         label: 'Delivery',         pill: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',             dot: 'bg-sky-500',     hex: '#0ea5e9', ring: 'ring-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300' },
  site_update:      { icon: HardHat,       label: 'Site Update',      pill: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300', dot: 'bg-purple-500',  hex: '#a855f7', ring: 'ring-purple-500/40 bg-purple-500/10 text-purple-700 dark:text-purple-300' },
  quality:          { icon: ShieldCheck,   label: 'Quality',          pill: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',         dot: 'bg-teal-500',    hex: '#14b8a6', ring: 'ring-teal-500/40 bg-teal-500/10 text-teal-700 dark:text-teal-300' },
  safety:           { icon: AlertCircle,   label: 'Safety',           pill: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',             dot: 'bg-red-500',     hex: '#ef4444', ring: 'ring-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300' },
  variation:        { icon: Briefcase,     label: 'Variation',        pill: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',     dot: 'bg-amber-500',   hex: '#f59e0b', ring: 'ring-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300' },
  timesheet:        { icon: Timer,         label: 'Timesheet',        pill: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',         dot: 'bg-pink-500',    hex: '#ec4899', ring: 'ring-pink-500/40 bg-pink-500/10 text-pink-700 dark:text-pink-300' },
  general:          { icon: Mail,          label: 'General',          pill: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',            dot: 'bg-gray-400',    hex: '#9ca3af', ring: 'ring-gray-500/40 bg-gray-500/10 text-gray-700 dark:text-gray-300' },
  notification:     { icon: Bell,          label: 'System',           pill: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',        dot: 'bg-slate-400',   hex: '#94a3b8', ring: 'ring-slate-500/40 bg-slate-500/10 text-slate-600 dark:text-slate-300' },
  marketing:        { icon: Newspaper,     label: 'Marketing',        pill: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-300', dot: 'bg-fuchsia-500', hex: '#d946ef', ring: 'ring-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300' },
  spam:             { icon: Megaphone,     label: 'Promotion',        pill: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',         dot: 'bg-rose-400',    hex: '#fb7185', ring: 'ring-rose-500/40 bg-rose-500/10 text-rose-700 dark:text-rose-300' },
  junk:             { icon: Ban,           label: 'Junk',             pill: 'bg-rose-200 text-rose-900 dark:bg-rose-950/50 dark:text-rose-300',         dot: 'bg-rose-800',    hex: '#9f1239', ring: 'ring-rose-700/40 bg-rose-700/10 text-rose-800 dark:text-rose-300' },
  uncategorized:    { icon: Sparkles,      label: 'Uncategorised',    pill: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',            dot: 'bg-zinc-400',    hex: '#a1a1aa', ring: 'ring-zinc-500/40 bg-zinc-500/10 text-zinc-700 dark:text-zinc-300' },
}

/**
 * Display order for the always-on category filter bar above the email
 * list. Sorted by what construction-business users care about most:
 * the money flow first (quote → contract → invoice → payment), then
 * operational signals (suppliers, deliveries, site updates), then
 * miscellaneous, ending with low-signal buckets.
 */
export const CATEGORY_BAR_ORDER: string[] = [
  'bill',
  'quote_request',
  'quote_submission',
  'contract',
  'invoice',
  'payment',
  'variation',
  'supplier',
  'delivery',
  'site_update',
  'quality',
  'safety',
  'timesheet',
  'general',
  'notification',
  'marketing',
  'spam',
  'junk',
  'uncategorized',
]

/**
 * Categories the "Promotions hidden" visibility toggle suppresses. The AI
 * stores newsletters as ``marketing`` (``newsletter`` is normalised to it
 * upstream), while low-signal blasts land in ``spam`` (labelled "Promotion"
 * in the UI). Both are promotional noise, so one toggle hides the whole set.
 * ``newsletter`` is listed defensively in case an un-normalised value ever
 * reaches the list; ``marketing`` is the key that matters in practice.
 */
export const PROMO_CATEGORIES: string[] = ['spam', 'marketing', 'newsletter']

/**
 * The "Important only" quick preset — the business-critical categories a
 * user means by "I only want to see important emails eg from supplier,
 * clients, internal team etc.". Money flow (quotes → contract → invoice →
 * bill → payment) plus active client/supplier work (supplier, variation,
 * site updates). Deliberately excludes low-signal buckets (general,
 * notification, marketing, spam, junk) and operational noise the user did
 * not ask to prioritise (timesheet, delivery, quality, safety). There is no
 * dedicated "internal team" category — that is an allocation/counterparty
 * concept, out of scope for a category filter.
 */
export const IMPORTANT_CATEGORIES: string[] = [
  'quote_request',
  'quote_submission',
  'contract',
  'bill',
  'invoice',
  'payment',
  'supplier',
  'variation',
  'site_update',
]

/**
 * Pure: is ANY promotional chip explicitly selected? When true the
 * "Promotions hidden" toggle is hidden and treated as inactive, because
 * selecting a promo chip while excluding it would cancel to an empty list.
 * Mirrors the pre-existing single-``spam`` intent, generalised to the set.
 */
export function isPromoSelected(selectedCategories: string[]): boolean {
  return PROMO_CATEGORIES.some((k) => selectedCategories.includes(k))
}

/**
 * Pure exclusion builder for the inbox ``category__not`` param. Extracted
 * from InboxPage so the mutual-exclusion logic is unit-testable without
 * React. When ``hidePromotions`` is on, every promo key NOT explicitly
 * selected is excluded (so picking the Promotion/Marketing chip reveals
 * that specific key instead of cancelling to an empty list). Muted
 * categories ride the same param with the same explicit-selection override.
 * Returns a sorted array, or ``undefined`` when nothing is excluded.
 */
export function buildExcludeCategories(
  hidePromotions: boolean,
  selectedCategories: string[],
  mutedCategories: string[],
): string[] | undefined {
  const excluded = new Set<string>()
  if (hidePromotions) {
    for (const key of PROMO_CATEGORIES) {
      if (!selectedCategories.includes(key)) excluded.add(key)
    }
  }
  for (const key of mutedCategories) {
    if (!selectedCategories.includes(key)) excluded.add(key)
  }
  return excluded.size > 0 ? Array.from(excluded).sort() : undefined
}

/**
 * Always prefer these on the compact category bar (money flow). The rest
 * of CATEGORY_BAR_ORDER fills remaining slots by volume, then overflows
 * into the searchable More menu — see CategoryFilterBar.
 */
export const CATEGORY_PINNED_PRIMARY: string[] = [
  'bill',
  'quote_request',
  'quote_submission',
]

/** Max category chips on the compact bar before overflow → More menu. */
export const CATEGORY_INLINE_MAX = 5

/**
 * Pin money-flow categories, fill remaining slots by volume, keep any
 * active selection visible. Pure — unit-tested without React.
 */
export function pickInlineCategoryKeys(
  visibleKeys: string[],
  byCategory: Record<string, number> | undefined,
  selectedCategories: string[],
  maxInline = CATEGORY_INLINE_MAX,
): string[] {
  const pinned = CATEGORY_PINNED_PRIMARY.filter((k) => visibleKeys.includes(k))
  const rest = visibleKeys
    .filter((k) => !pinned.includes(k))
    .sort((a, b) => (byCategory?.[b] ?? 0) - (byCategory?.[a] ?? 0))
  const slots = Math.max(0, maxInline - pinned.length)
  const filled = [...pinned, ...rest.slice(0, slots)]
  for (const key of selectedCategories) {
    if (visibleKeys.includes(key) && !filled.includes(key)) filled.push(key)
  }
  return filled
}

/** A custom label (the "Personal" tier's `personal.<key>` OR the
 *  org-shared custom tier's `org.<key>`), applied to an email as a
 *  namespaced category. The visual config is resolved here so the list +
 *  reader render it with the chosen colour and a consistent treatment. */
export interface PersonalCategoryLike {
  slug: string
  label: string
  color?: string
}

/** Shared "personal" pill/dot/ring styling (indigo) — keeps every personal
 *  label visually distinct from the org/AI taxonomy. The user's chosen
 *  colour drives the inline `hex` (frame accent + chip dot via dotColor). */
const PERSONAL_STYLE = {
  icon: User,
  pill: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  dot: 'bg-indigo-500',
  ring: 'ring-indigo-500/40 bg-indigo-500/10 text-indigo-700 dark:text-indigo-300',
} as const

/** Org-shared custom labels get a cyan treatment so they read as "team"
 *  (vs indigo = private personal) while staying distinct from built-ins. */
const ORG_CUSTOM_STYLE = {
  icon: Users,
  pill: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
  dot: 'bg-cyan-600',
  ring: 'ring-cyan-600/40 bg-cyan-600/10 text-cyan-800 dark:text-cyan-300',
} as const

/** Pure: `personal.home_bills` / `org.site_access` → "Home Bills" /
 *  "Site Access" (used when the live label isn't loaded / the category
 *  was deleted but still stamped on a row). */
function humaniseNamespacedKey(key: string, prefix: string): string {
  return key
    .slice(prefix.length)
    .replace(/[._]/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase())
    .trim()
}

/**
 * Resolve a category key to its visual config, falling back to a safe
 * default rather than throwing on unknown keys (e.g. if the backend
 * ships a new category before the frontend ships matching styling).
 *
 * Pass ``customCategories`` (the viewer's private tier + the org custom
 * tier — see ``usePersonalCategories`` / ``useOrgCustomCategories``; a
 * combined array is fine, resolution matches on slug) so an applied
 * ``personal.<key>`` / ``org.<key>`` resolves to its real label + colour.
 * Omit it on org-only surfaces (the filter bar); a namespaced key then
 * renders with a humanised label + the tier's default hue.
 */
export function getCategoryConfig(
  key: string | undefined | null,
  customCategories?: PersonalCategoryLike[],
): CategoryConfig {
  if (!key) return CATEGORY_CONFIG.uncategorized
  if (key.startsWith('personal.')) {
    const match = customCategories?.find((c) => c.slug === key)
    return {
      ...PERSONAL_STYLE,
      label: match?.label || humaniseNamespacedKey(key, 'personal.'),
      hex: match?.color || '#6366f1',
    }
  }
  if (key.startsWith('org.')) {
    const match = customCategories?.find((c) => c.slug === key)
    return {
      ...ORG_CUSTOM_STYLE,
      label: match?.label || humaniseNamespacedKey(key, 'org.'),
      hex: match?.color || '#0891b2',
    }
  }
  return (
    CATEGORY_CONFIG[key] || {
      ...CATEGORY_CONFIG.general,
      label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
    }
  )
}
